// dependency-graph-ignore-file
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { FireParams, MetricConfig, MetricMetadataBase } from './define-metric';
import { fireMetric } from './fire-metric';
import { cancelMetric, createMetricRef } from './stabilized-scheduler';

/**
 * React hook for firing a stabilized metric.
 *
 * Holds one {@link MetricRef} for the hook’s lifetime (same object returned from
 * `useMemo(() => createMetricRef(), [])`), matching `fireMetric` / `runStabilizedScheduler`.
 * On unmount, calls
 * {@link cancelMetric} so teardown does not emit a reliability row. `cancelMetric` never
 * publishes; supersede and lifecycle paths are all silent.
 *
 * Returns a `fire` function to trigger the metric with given params.
 *
 * Include `contentId` in `params.metadata` when you need database size fields on the report.
 */
export function useMetric<
  TData,
  TMetadata extends MetricMetadataBase = MetricMetadataBase
>(
  _config: MetricConfig<TData, TMetadata>
): {
  fire: (params: FireParams<TData, TMetadata>) => void;
} {
  const metricRef = useMemo(() => createMetricRef(), []);
  const configRef = useRef(_config);
  configRef.current = _config;

  useEffect(() => {
    return () => {
      cancelMetric(metricRef);
    };
  }, [metricRef]);

  const fire = useCallback(
    (params: FireParams<TData, TMetadata>) => {
      fireMetric(configRef.current, params, metricRef);
    },
    [metricRef]
  );

  return { fire };
}
